import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../../api/config';

/**
 * LinkedIn OAuth Callback Handler
 * 
 * This component handles the OAuth callback from LinkedIn.
 * 1. Extracts the authorization code from URL
 * 2. Calls backend to exchange code for access token
 * 3. Backend saves token to database
 * 4. Redirects to frontend with success/error
 */
const LinkedInCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('Processing authorization...');

  useEffect(() => {
    // Add a safety timeout - if nothing happens in 10 seconds, redirect with error
    const safetyTimeout = setTimeout(() => {
      setStatus('Timeout - redirecting...');
      navigate('/lead_sources_capture?linkedin_error=' + encodeURIComponent('Callback timeout - please try again'), { replace: true });
    }, 10000);

    const handleCallback = async () => {
      try {
        // Step 1: Extract code from query parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');


        // Handle LinkedIn errors
        if (error) {
          clearTimeout(safetyTimeout);
          setStatus('Authorization failed');
          const errorMsg = errorDescription || error || 'OAuth authorization failed';
          navigate(`/lead_sources_capture?linkedin_error=${encodeURIComponent(errorMsg)}`, { replace: true });
          return;
        }

        // Validate code exists
        if (!code) {
          clearTimeout(safetyTimeout);
          setStatus('No authorization code received');
          navigate('/lead_sources_capture?linkedin_error=' + encodeURIComponent('Authorization code is required'), { replace: true });
          return;
        }

        // Step 2: Exchange code for token (backend handles this)
        setStatus('Exchanging authorization code for access token...');

        // Use API_BASE_URL from config (same as rest of the app)
        // This ensures consistency with other API calls
        const backendUrl = process.env.REACT_APP_API_BASE_URL;
        const callbackUrl = `${backendUrl}/api/crm/linkedin/oauth/callback`;

        let response;
        try {
          setStatus('Calling backend API...');

          response = await axios.post(
            callbackUrl,
            { code, state },
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              timeout: 30000, // 30 second timeout
              validateStatus: function (status) {
                // Accept all status codes (don't throw on 4xx/5xx)
                return status >= 200 && status < 600;
              },
              // Add withCredentials if needed for CORS
              withCredentials: false
            }
          );
        } catch (axiosError) {

          // If it's a network error (no response), show specific message
          if (!axiosError.response) {
            setStatus('Network error - cannot reach backend');
            throw new Error(`Cannot connect to backend at ${callbackUrl}. Please check if the backend server is running.`);
          }

          throw axiosError;
        }

        // Step 3: Check if token was saved successfully

        // Step 3: Check if token was saved successfully
        clearTimeout(safetyTimeout); // Clear safety timeout on success
        const responseData = response.data || {};

        // Check for success (responseCode 200 or HTTP status 200)
        if (responseData.responseCode === 200 || response.status === 200) {
          setStatus('Authorization successful! Redirecting...');

          // Step 4: Redirect to frontend with success immediately
          navigate('/lead_sources_capture?linkedin_success=true', { replace: true });
        } else {
          clearTimeout(safetyTimeout);
          const errorMsg = responseData.responseMessage || responseData.error || 'Failed to exchange authorization code';
          setStatus(`Token exchange failed: ${errorMsg}`);
          navigate(`/lead_sources_capture?linkedin_error=${encodeURIComponent(errorMsg)}`, { replace: true });
        }
      } catch (error) {
        clearTimeout(safetyTimeout);

        // Determine error message
        let errorMsg = 'Failed to process OAuth callback';
        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
          errorMsg = 'Cannot connect to backend server. Please check if the server is running.';
        } else if (error.response) {
          errorMsg = error.response.data?.responseMessage
            || error.response.data?.error_description
            || error.response.data?.error
            || error.message;
        } else if (error.message) {
          errorMsg = error.message;
        }

        setStatus(`Error: ${errorMsg}`);

        // Redirect immediately with error
        navigate(`/lead_sources_capture?linkedin_error=${encodeURIComponent(errorMsg)}`, { replace: true });
      }
    };

    // Execute callback immediately
    handleCallback();

    // Cleanup function
    return () => {
      clearTimeout(safetyTimeout);
    };
  }, [searchParams, navigate]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: '1rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{ fontSize: '1.25rem', fontWeight: 500 }}>LinkedIn callback working</div>
      <div style={{ fontSize: '0.875rem', color: '#666' }}>{status}</div>
    </div>
  );
};

export default LinkedInCallback;

